import React, { useState, lazy, Component } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, ChevronRightIcon, UsersIcon, FilterIcon, StarIcon, BookOpenIcon, GiftIcon, BookIcon, FlameIcon, MoonIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { allBooks } from '../data/booksData';
import { useAfterTideFilter } from '../hooks/useAfterTideFilter';
import { abbreviateGenre } from '../utils/genreAbbreviations';
// Helper function to get color for genre
const getGenreColor = (genre: string): string => {
  const genreLower = genre.toLowerCase();
  if (genreLower.includes('romance')) return 'bg-pink-600';
  if (genreLower.includes('fantasy')) return 'bg-indigo-600';
  if (genreLower.includes('sci-fi') || genreLower.includes('science')) return 'bg-blue-600';
  if (genreLower.includes('mystery')) return 'bg-indigo-600';
  if (genreLower.includes('thriller')) return 'bg-red-600';
  if (genreLower.includes('horror')) return 'bg-gray-800';
  if (genreLower.includes('historical')) return 'bg-amber-600';
  return 'bg-violet-600';
};
// Spice/Darkness level maps
const spiceLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Mild',
    color: 'bg-sky-500/30 text-sky-200'
  },
  2: {
    label: 'Warm',
    color: 'bg-amber-500/30 text-amber-200'
  },
  3: {
    label: 'Hot',
    color: 'bg-orange-500/30 text-orange-200'
  },
  4: {
    label: 'Steamy',
    color: 'bg-rose-500/30 text-rose-200'
  },
  5: {
    label: 'Scorching',
    color: 'bg-red-500/30 text-red-200'
  }
};
const darknessLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Shadow',
    color: 'bg-slate-400/30 text-slate-200'
  },
  2: {
    label: 'Grey',
    color: 'bg-slate-500/30 text-slate-200'
  },
  3: {
    label: 'Abyss',
    color: 'bg-gray-600/30 text-gray-200'
  },
  4: {
    label: 'Pitch Black',
    color: 'bg-gray-700/40 text-gray-100'
  },
  5: {
    label: 'The Void',
    color: 'bg-gray-800/50 text-white'
  }
};
// Anthology Book Card Component with multi-author overlay
interface AnthologyCardProps {
  anthology: (typeof allBooks)[0];
}
const AnthologyCard: React.FC<AnthologyCardProps> = ({
  anthology
}) => {
  const [imgError, setImgError] = useState(false);
  const rt = useRoleTheme();
  // Format authors for display (show up to 3, then "+ X more")
  const contributors = anthology.contributors || [];
  const maxVisible = 3;
  const visibleAuthors = contributors.slice(0, maxVisible);
  const remainingCount = contributors.length - maxVisible;
  const authorsDisplay = visibleAuthors.map(c => c.authorName).join(', ');
  const authorsWithMore = remainingCount > 0 ? `${authorsDisplay} + ${remainingCount} more` : authorsDisplay;
  return <div className="relative group w-full" data-id="element-5501" data-mp="z3un6evqc342">
      <Link to={`/anthology/${anthology.id}`} className="block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover:scale-105" data-id="element-5502" data-mp="v3j1cyrmrwr0">
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-5503" data-mp="lej17c1e6vb8">
            <BookIcon className="h-10 w-10 text-indigo-400 mb-2" data-id="element-5504" data-mp="gcjwnytx1scp" />
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-2" data-id="element-5505" data-mp="beihhozm2d6i">
              {anthology.title}
            </span>
          </div> : <img src={anthology.coverImage} alt={anthology.title} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-5506" data-mp="2fes639up1jk" />}

        {/* Genre tag top-left */}
        {anthology.genres[0] && <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(anthology.genres[0])} px-2 py-0.5 rounded-full`} data-id="element-5507" data-mp="thquo83m97bv">
            {abbreviateGenre(anthology.genres[0])}
          </span>}

        {/* Anthology badge top-right */}
        <span className="absolute top-2 right-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md" data-id="element-5508" data-mp="pg5b63wktusp">
          <UsersIcon className="h-3 w-3" data-id="element-5509" data-mp="apy38i473u69" />
          {contributors.length}
        </span>

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col p-3 transition-opacity duration-200 text-white z-20 opacity-0 group-hover:opacity-100" data-id="element-5510" data-mp="fkapezx23jlp">
          {/* Title */}
          <h3 className="font-bold text-white text-sm line-clamp-2 mb-1" data-id="element-5511" data-mp="5qbpm7kggbwx">
            {anthology.title}
          </h3>

          {/* Multi-author byline */}
          <p className="text-white/80 text-xs line-clamp-2 mb-2" data-id="element-5512" data-mp="3sw2mig6h58sm">
            by {authorsWithMore}
          </p>

          {/* Subgenres/Tags */}
          <div className="flex flex-wrap gap-1 mb-2" data-id="element-5513" data-mp="39dexgnf5r7k8">
            {anthology.genres.slice(0, 2).map((genre, idx) => <span key={idx} className={`px-1.5 py-0.5 ${getGenreColor(genre)}/80 text-white rounded text-[10px]`} data-id="element-5514" data-mp="3j98gmpndtzaz" data-mpi={idx}>
                {abbreviateGenre(genre)}
              </span>)}
          </div>

          {/* Spice & Darkness */}
          <div className="flex flex-wrap gap-1 mb-2" data-id="element-5515" data-mp="3nb3h5rxho8np">
            {anthology.spiceLevel > 0 && spiceLevelMap[anthology.spiceLevel] && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${spiceLevelMap[anthology.spiceLevel].color}`} data-id="element-5516" data-mp="5takemzyvx4r">
                  <FlameIcon className="h-2.5 w-2.5" data-id="element-5517" data-mp="evtheqh2nfj3" />
                  {spiceLevelMap[anthology.spiceLevel].label}
                </span>}
            {anthology.darknessLevel > 0 && darknessLevelMap[anthology.darknessLevel] && <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${darknessLevelMap[anthology.darknessLevel].color}`} data-id="element-5518" data-mp="mvgcmxnmof1p">
                  <MoonIcon className="h-2.5 w-2.5" data-id="element-5519" data-mp="wm1666og2tdr" />
                  {darknessLevelMap[anthology.darknessLevel].label}
                </span>}
          </div>

          {/* Spacer */}
          <div className="flex-1" data-id="element-5520" data-mp="15loab08k6dai" />

          {/* Bottom: Rating & Stories count */}
          <div className="flex items-center justify-between mt-auto" data-id="element-5521" data-mp="1altw3zs5d5ko">
            <div className="flex items-center gap-1" data-id="element-5522" data-mp="1ajprffmfc2jc">
              <StarIcon className="h-4 w-4 text-amber-400" data-id="element-5523" data-mp="1fjlwzjars210" />
              <span className="text-[11px]" data-id="element-5524" data-mp="3ccx8oullku7v">
                {anthology.rating > 0 ? anthology.rating : 'New'}
              </span>
              {anthology.reviewCount && anthology.reviewCount > 0 && <span className="text-gray-400 text-[11px]" data-id="element-5525" data-mp="1ygnhkov1ppan">
                  ({anthology.reviewCount})
                </span>}
            </div>
            <span className="text-[10px] text-white/70" data-id="element-5526" data-mp="1tovzz3tvatwn">
              {contributors.length} stories
            </span>
          </div>
        </div>
      </Link>
    </div>;
};
const Anthologies = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    filterContent
  } = useAfterTideFilter();
  const [searchTerm, setSearchTerm] = useState('');
  // Sidebar state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('popularity');
  const activeFilterCount = selectedGenres.length + selectedFeatures.length + (sortBy !== 'popularity' ? 1 : 0);
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Most Popular'
    }, {
      value: 'newest',
      label: 'Newest First'
    }, {
      value: 'raised',
      label: 'Most Funds Raised'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'features',
    title: 'Features',
    type: 'checkbox',
    options: [{
      value: 'charity',
      label: 'Charity Anthology'
    }, {
      value: 'limited',
      label: 'Limited Time Only'
    }, {
      value: 'indie',
      label: 'Indie Authors'
    }],
    value: selectedFeatures,
    onChange: (val: string) => setSelectedFeatures(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Thriller',
      label: 'Thriller'
    }, {
      value: 'Sci-Fi',
      label: 'Science Fiction'
    }, {
      value: 'Horror',
      label: 'Horror'
    }],
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedFeatures([]);
    setSortBy('popularity');
    setSearchTerm('');
  };
  // Get all anthologies from the catalog (filtered by platform)
  const allAnthologies = filterContent(allBooks).filter(b => b.isAnthology);
  // Apply filters
  const applyFilters = (data: typeof allAnthologies) => {
    let result = [...data];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(a => a.title.toLowerCase().includes(q) || a.contributors?.some(c => c.authorName.toLowerCase().includes(q)));
    }
    if (selectedGenres.length > 0) {
      result = result.filter(a => a.genres.some(g => selectedGenres.includes(g)));
    }
    // Mock sorting
    if (sortBy === 'newest') {
      result.reverse();
    }
    return result;
  };
  const filteredAnthologies = applyFilters(allAnthologies);
  const featuredAnthology = allAnthologies.find(a => a.id === 'anthology-1');
  return <div className={`min-h-screen ${dk.page}`} data-id="element-5527" data-mp="117cz1wbeqaam">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-5528" data-mp="30j5u3nemx5kp">
        <div className="max-w-7xl mx-auto" data-id="element-5529" data-mp="30gjqfyuv7a7r">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-5530" data-mp="2vjjjzmu3s5g4">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-5531" data-mp="333lp7m01598j">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5532" data-mp="380kgzxw8143k" />
            <Link to="/browse" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-5533" data-mp="3csvkfaq6780v">
              Books
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5534" data-mp="2k4g8ix2c55tr" />
            <span className={dk.textPrimary} data-id="element-5535" data-mp="2k6114h53aeoz">Anthologies</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-5536" data-mp="2p4m3am3wj8g2">
            <div className="flex items-center gap-3" data-id="element-5537" data-mp="2u1kvgz4095bm">
              <UsersIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-5538" data-mp="2u50c46dbumf5" />
              <div data-id="element-5539" data-mp="2y5slczm78rbf">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-5540" data-mp="2y24sm2okw8xr">
                  Anthologies & Collections
                </h1>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-5541" data-mp="dc70vxrr5htv">
                  Discover multi-author collections, charity anthologies, and
                  shared worlds.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-5542" data-mp="2jfcb8y8tkc7k">
              <div className="relative flex-grow md:w-80" data-id="element-5543" data-mp="2jd8ceups23e5">
                <input type="text" placeholder="Search anthologies or authors..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full px-5 py-2.5 pl-10 rounded-lg border focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.input}`} data-id="element-5544" data-mp="2ehaw2oeikx1d" />
                <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-5545" data-mp="1vq06cakiyqb2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5546" data-mp="12z015wagjght">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-5547" data-mp="3emlbdren01b7">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-5548" data-mp="3jfefqe6717mr">
            <FilterIcon className="h-4 w-4" data-id="element-5549" data-mp="3anw3gecdbbyy" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-5550" data-mp="28cehriqcfpp">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-5551" data-mp="3tdc4r3vvn8gf">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-5552" data-mp="3tbrazghdztjm">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-5553" data-mp="2x8c357mi5f2c" />
          </FilterSidebarWrapper>

          {/* Discovery Sections */}
          <div className="flex-1 min-w-0 space-y-12" data-id="element-5554" data-mp="2sfsqccvn2nyl">
            {/* Featured Anthology Banner */}
            {featuredAnthology && <section className={`rounded-xl overflow-hidden border ${dk.border} ${dk.card}`} data-id="element-5555" data-mp="2jgnsl8rwiynh">
                <div className="flex flex-col md:flex-row md:max-h-64" data-id="element-5556" data-mp="3g5mzajcu7sdk">
                  <div className="w-full md:w-1/3 relative" data-id="element-5557" data-mp="3c5cl4jew5vnm">
                    <img src={featuredAnthology.coverImage} alt={featuredAnthology.title} className="w-full h-48 md:h-full object-cover" data-id="element-5558" data-mp="3c2yq2ak7ojg2" />
                    <div className="absolute top-3 left-3 flex flex-col gap-2" data-id="element-5559" data-mp="1vqtg4sbhvc3r">
                      {featuredAnthology.genres[0] && <span className={`text-white text-xs font-medium px-2 py-0.5 rounded-full w-fit ${getGenreColor(featuredAnthology.genres[0])}`} data-id="element-5560" data-mp="1qqouknvqbr6o">
                          {featuredAnthology.genres[0]}
                        </span>}
                      <span className="bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 w-fit shadow-md" data-id="element-5561" data-mp="1qkejcjvfxubf">
                        <GiftIcon className="h-3 w-3" data-id="element-5562" data-mp="1zejgtm52y3zx" /> Charity
                      </span>
                    </div>
                  </div>
                  <div className={`w-full md:w-2/3 p-5 md:p-6 flex flex-col justify-between ${dk.card}`} data-id="element-5563" data-mp="24c2uajtyecqt">
                    <div data-id="element-5564" data-mp="29ameaeozlegj">
                      <div className="flex items-center gap-2 mb-2" data-id="element-5565" data-mp="2ec3gcdj7j6yp">
                        <span className={`text-xs font-bold uppercase tracking-wide text-rose-500`} data-id="element-5566" data-mp="2e97iep7d0ia9">
                          Featured Charity Anthology
                        </span>
                        <span className={`text-xs font-medium ${dk.textMuted}`} data-id="element-5567" data-mp="bw1mizbh7k24">
                          • {featuredAnthology.contributors?.length || 0}{' '}
                          Authors
                        </span>
                      </div>
                      <h2 className={`text-xl md:text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-5568" data-mp="lsxwl9h0we8s">
                        {featuredAnthology.title}
                      </h2>
                      <p className={`text-sm mb-3 ${dk.textSecondary}`} data-id="element-5569" data-mp="pwm3rr1qt7ti">
                        by{' '}
                        {featuredAnthology.contributors?.slice(0, 3).map(c => c.authorName).join(', ')}
                        {(featuredAnthology.contributors?.length || 0) > 3 && ` + ${(featuredAnthology.contributors?.length || 0) - 3} more`}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 mt-auto" data-id="element-5570" data-mp="1s95gwco51dl7">
                      <div className="flex items-center gap-1" data-id="element-5571" data-mp="1x804drx1vqbn">
                        <StarIcon className="h-4 w-4 text-amber-400 fill-amber-400" data-id="element-5572" data-mp="1x777rjrt2h78" />
                        <span className={`text-sm font-semibold ${dk.textPrimary}`} data-id="element-5573" data-mp="1j7fpv7y64gny">
                          {featuredAnthology.rating}
                        </span>
                      </div>
                      <Link to={`/anthology/${featuredAnthology.id}`} className={`flex items-center justify-center px-5 py-2 rounded-lg font-medium text-sm text-white transition-colors ${rt.primaryButton}`} data-id="element-5574" data-mp="1e94doqkehu2k">
                        View Anthology
                      </Link>
                    </div>
                  </div>
                </div>
              </section>}

            {/* All Anthologies Grid */}
            <section data-id="element-5575" data-mp="1b0nyoox0pahb">
              <div className="flex items-center justify-between mb-6" data-id="element-5576" data-mp="1b163wb1nqz1d">
                <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-5577" data-mp="1aya3mg2c7y9r">
                  {searchTerm ? 'Search Results' : 'All Anthologies'}
                </h2>
                <span className={`text-sm ${dk.textMuted}`} data-id="element-5578" data-mp="11yeoy19kpl4z">
                  {filteredAnthologies.length} results
                </span>
              </div>

              {filteredAnthologies.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-id="element-5579" data-mp="s33azl5zohya">
                  {filteredAnthologies.map(anthology => <AnthologyCard key={anthology.id} anthology={anthology} data-id="element-5580" data-mp="ics7fzq7warx" />)}
                </div> : <div className="text-center py-16 border rounded-xl border-dashed border-gray-300 dark:border-gray-700" data-id="element-5581" data-mp="e8kf7skjwcoy">
                  <UsersIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-5582" data-mp="4chaceybdzyb" />
                  <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-5583" data-mp="2fyhuawn7pnzn">
                    No anthologies found
                  </h3>
                  <p className={`mb-4 ${dk.textSecondary}`} data-id="element-5584" data-mp="2l0qplznk524x">
                    Try adjusting your search or filters.
                  </p>
                  <button onClick={resetFilters} className={`font-medium ${rt.accentLink}`} data-id="element-5585" data-mp="2tzu74z72t9hb">
                    Clear filters
                  </button>
                </div>}
            </section>

            {/* Bottom CTA */}
            <div className={`rounded-xl p-8 text-center border ${dk.border} ${rt.bgLight}`} data-id="element-5586" data-mp="269hsz6l8mgia">
              <GiftIcon className={`h-12 w-12 mx-auto mb-4 text-rose-500`} data-id="element-5587" data-mp="2b19nfs7il6dp" />
              <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-5588" data-mp="1ejdchj0lmdmc">
                Organizing an Anthology?
              </h2>
              <p className={`max-w-lg mx-auto mb-6 ${dk.textSecondary}`} data-id="element-5589" data-mp="1iim68nvn2bhw">
                Use our Product Manager to easily invite contributors, collect
                stories, and automatically route royalties to charity.
              </p>
              <Link to="/vendors/product-manager" className={`inline-flex items-center px-6 py-3 rounded-lg font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 bg-rose-600 hover:bg-rose-700`} data-id="element-5590" data-mp="yzngqu8p4wvn">
                Create an Anthology
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Anthologies;